/**
 * Direct Classification Form Component
 * Form for single product classification with Quick / Advanced modes
 */
import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Tooltip,
  Fade,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import {
  AutoAwesome as AIIcon,
  CheckCircle as SuccessIcon,
  ContentCopy as CopyIcon,
  Science as ScienceIcon,
  Route as RouteIcon,
  CheckCircleOutline as VerifiedIcon,
  Refresh as ResetIcon,
  Flag as FlagIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import {
  DirectClassificationResponse,
  AdvancedClassificationResponse,
  TariffHierarchy,
  TariffHierarchyLevel,
  LegalChild,
  getLegalChildren,
  getChapters,
  submitTariffFeedback,
} from '../../../services/tariffClassifierPageService';
import { useTranslation } from '../../../store/languageStore';
import { useAuthStore } from '../../../store/authStore';
import { brand } from '../../../theme';
import ShippingVerdictPanel from './ShippingVerdictPanel';
import HierarchyLadder from './HierarchyLadder';
import { toast } from 'react-toastify';

type CorrectionLevel = 'chapter' | 'heading' | 'subheading' | 'cn8';
const LEVEL_DIGITS: Record<CorrectionLevel, number> = { chapter: 2, heading: 4, subheading: 6, cn8: 8 };
const LEVEL_LABEL: Record<CorrectionLevel, string> = {
  chapter: 'Chapter (2-digit)',
  heading: 'Heading (4-digit)',
  subheading: 'Subheading (6-digit)',
  cn8: 'Full code (8-digit)',
};

interface DirectClassificationFormProps {
  onClassify: (data: {
    product_description: string;
  }) => Promise<DirectClassificationResponse | AdvancedClassificationResponse>;
  loading?: boolean;
}

// Type guard for Advanced Response — must be null-safe: JS `in` operator throws when RHS is null
function isAdvancedResponse(res: any): res is AdvancedClassificationResponse {
  return res != null && ('rag_details' in res || 'chapter_name' in res);
}

const pathConfig: Record<string, { label: string; color: 'success' | 'warning' | 'info' | 'default'; desc: string }> = {
  quick:              { label: '⚡ Quick',    color: 'success', desc: 'Vector search + local reranker — no LLM' },
  local_rerank:       { label: '⚡ Quick',    color: 'success', desc: 'Vector search + local reranker — no LLM' },
  llm_judge:          { label: '🔬 Advanced', color: 'warning', desc: 'Mistral LLM judge selected the best match' },
  llm_judge_fallback: { label: '🔬 Advanced', color: 'warning', desc: 'LLM judge unavailable — used top vector match' },
};

const DirectClassificationForm: React.FC<DirectClassificationFormProps> = ({
  onClassify,
  loading = false,
}) => {
  const { t, currentLanguage } = useTranslation();
  const user = useAuthStore((s) => s.user);

  const [formData, setFormData] = useState({ product_description: '' });
  const [result, setResult] = useState<DirectClassificationResponse | AdvancedClassificationResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Feedback dialog state (level-aware)
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [correctionLevel, setCorrectionLevel] = useState<CorrectionLevel>('cn8');
  const [correctHsCode, setCorrectHsCode] = useState('');
  const [correctHsCodeError, setCorrectHsCodeError] = useState('');
  const [feedbackNote, setFeedbackNote] = useState('');
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  // Quick-pick options for the corrected level: the rung's own alternatives
  // plus the legal siblings fetched from the parent prefix.
  const [childOptions, setChildOptions] = useState<LegalChild[]>([]);
  const [loadingChildren, setLoadingChildren] = useState(false);
  // Codes the model recommended (the rung's alternatives) — highlighted in the
  // picker as "recommended"; the rest are the full set of valid options.
  const [recommendedCodes, setRecommendedCodes] = useState<string[]>([]);

  const hasFeedback = !!user?.has_tariff_feedback;

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.product_description.trim()) {
      setError('Product description is required');
      return;
    }
    if (formData.product_description.trim().length < 5) {
      setError('Product description must be at least 5 characters');
      return;
    }
    setError(null);
    setResult(null);
    try {
      const response = await onClassify(formData);
      setResult(response);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Classification failed. Please try again.');
    }
  };

  const handleCopyHSCode = () => {
    const code = isAdvancedResponse(result) ? result?.hs_code : result?.generated_hs_code;
    if (code) {
      navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleReset = () => {
    setFormData({ product_description: '' });
    setResult(null);
    setError(null);
  };

  const resetFeedbackFields = () => {
    setCorrectHsCode('');
    setCorrectHsCodeError('');
    setFeedbackNote('');
    setChildOptions([]);
    setRecommendedCodes([]);
  };

  // Legacy "Incorrect result?" button → correct the full 8-digit code.
  const handleFeedbackOpen = () => {
    resetFeedbackFields();
    setCorrectionLevel('cn8');
    setFeedbackOpen(true);
  };

  // Per-rung "Correct" button → correct at that hierarchy level. Seeds the
  // quick-pick list with the rung's alternatives, then fetches the legal
  // siblings under the parent prefix so the expert only sees valid codes.
  const handleCorrectLevel = async (lv: TariffHierarchyLevel) => {
    resetFeedbackFields();
    const level = lv.level as CorrectionLevel;
    setCorrectionLevel(level);
    setFeedbackOpen(true);

    // The model's alternatives are the "recommended" picks (shown first, starred).
    const seed: LegalChild[] = (lv.alternatives || []).map((a) => ({ code: a.code, title: a.title }));
    setChildOptions(seed);
    setRecommendedCodes(seed.map((s) => s.code));

    // Fetch the FULL valid set at this level so a correct code the model didn't
    // suggest is still one click away: all chapters for the chapter level (no
    // parent to enumerate), otherwise the legal children of the parent prefix.
    const digits = LEVEL_DIGITS[level];
    try {
      setLoadingChildren(true);
      const full =
        digits <= 2
          ? await getChapters(currentLanguage)
          : await getLegalChildren(lv.code.slice(0, digits - 2), currentLanguage);
      const seen = new Set(seed.map((s) => s.code));
      setChildOptions([...seed, ...full.filter((c) => !seen.has(c.code))]);
    } catch {
      /* keep the seeded alternatives if the fetch fails */
    } finally {
      setLoadingChildren(false);
    }
  };

  const handleFeedbackSubmit = async () => {
    const val = correctHsCode.trim();
    const digits = LEVEL_DIGITS[correctionLevel];
    const ok =
      correctionLevel === 'cn8' ? /^\d{6,10}$/.test(val) : new RegExp(`^\\d{${digits}}$`).test(val);
    if (!ok) {
      setCorrectHsCodeError(
        correctionLevel === 'cn8'
          ? 'Enter a valid HS code (6–10 digits, numbers only)'
          : `Enter a ${digits}-digit ${correctionLevel} code (numbers only)`
      );
      return;
    }
    setCorrectHsCodeError('');
    setFeedbackSubmitting(true);
    try {
      const suggestedCode = isAdvancedResponse(result) ? result?.hs_code : result?.generated_hs_code;
      const txnId = (result as any)?.transaction_id ?? null;
      await submitTariffFeedback({
        product_description: formData.product_description,
        correct_hs_code: val,
        correction_level: correctionLevel,
        suggested_hs_code: suggestedCode ?? undefined,
        transaction_id: txnId ?? undefined,
        user_note: feedbackNote.trim() || undefined,
      });
      setFeedbackOpen(false);
      toast.success('Correction submitted — the index has been updated immediately.');
    } catch (err: any) {
      const detail = err.response?.data?.detail || 'Failed to submit correction. Please try again.';
      toast.error(detail);
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  const getConfidenceColor = (c: number): 'success' | 'warning' | 'error' =>
    c >= 0.9 ? 'success' : c >= 0.7 ? 'warning' : 'error';

  const getConfidenceBg = (c: number) =>
    c >= 0.9 ? '#e8f5e9' : c >= 0.7 ? '#fff8e1' : '#fce4ec';

  const formatHSCode = (hsCode: string): string => {
    if (!hsCode) return '—';
    const clean = hsCode.replace(/[\s.-]/g, '');
    if (clean.length === 8) {
      return `${clean.slice(0, 2)}.${clean.slice(2, 4)}.${clean.slice(4, 6)}.${clean.slice(6, 8)}`;
    }
    return hsCode;
  };

  // Derived display values — all guarded against null result
  const hsCode = result ? (isAdvancedResponse(result) ? result.hs_code : result.generated_hs_code) : null;
  const description = result ? (isAdvancedResponse(result) ? result.description : result.original_description) : null;
  // pathTaken MUST be guarded: isAdvancedResponse(null) throws TypeError (JS `in` + null)
  const pathTaken = result ? (isAdvancedResponse(result) ? result.rag_details?.path_taken : 'quick') : null;
  const pathInfo = pathTaken ? pathConfig[pathTaken] ?? pathConfig['quick'] : null;
  const hierarchy: TariffHierarchy | null = (result as any)?.hierarchy ?? null;
  const currentLevelCode =
    hierarchy?.levels.find((l) => l.level === correctionLevel)?.code ?? (hsCode || '');
  const maxCodeLen = correctionLevel === 'cn8' ? 10 : LEVEL_DIGITS[correctionLevel];
  const charCount = formData.product_description.length;
  const isReady = formData.product_description.trim().length >= 5;

  return (
    <Box sx={{ width: '100%' }}>
      {/* Input Form */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
        }}
      >
        <form onSubmit={handleSubmit}>
          <Box sx={{ position: 'relative' }}>
            <TextField
              label={t('dashboard.cards.tariffClassifier.input.label')}
              multiline
              rows={4}
              fullWidth
              value={formData.product_description}
              onChange={handleChange('product_description')}
              placeholder={t('dashboard.cards.tariffClassifier.input.placeholder') as string}
              disabled={loading}
              inputProps={{ maxLength: 5000 }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1.5,
                  '&.Mui-focused': {
                    '& fieldset': { borderWidth: 2 },
                  },
                },
              }}
            />
            {/* Char counter */}
            <Typography
              variant="caption"
              color={charCount > 4800 ? 'error' : 'text.disabled'}
              sx={{ position: 'absolute', bottom: 8, right: 12 }}
            >
              {charCount}/5000
            </Typography>
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {t('dashboard.cards.tariffClassifier.input.helperText') as string}
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, mt: 2, alignItems: 'center' }}>
            <Tooltip title={!isReady ? 'Enter at least 5 characters to classify' : ''} arrow>
              <span>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <AIIcon />}
                  disabled={loading || !isReady}
                  sx={{
                    minWidth: 180,
                    borderRadius: 1,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    py: 1.2,
                    backgroundColor: brand.black,
                    color: '#fff',
                    '&:hover': { backgroundColor: '#000' },
                    '&.Mui-disabled': { backgroundColor: '#3A3A3A', color: '#888' },
                  }}
                >
                  {loading
                    ? t('dashboard.cards.tariffClassifier.classifying')
                    : t('dashboard.cards.tariffClassifier.classifyWithAI')}
                </Button>
              </span>
            </Tooltip>
            {(result || formData.product_description) && (
              <Button
                variant="outlined"
                onClick={handleReset}
                disabled={loading}
                startIcon={<ResetIcon />}
                sx={{
                  textTransform: 'none',
                  borderRadius: 1,
                  borderColor: brand.borderSubtle,
                  color: brand.textPrimary,
                  '&:hover': { borderColor: brand.orange, backgroundColor: 'transparent' },
                }}
              >
                {t('dashboard.cards.tariffClassifier.reset')}
              </Button>
            )}
          </Box>

          {/* Loading progress */}
          {loading && (
            <Box sx={{ mt: 2.5 }}>
              <LinearProgress sx={{ height: 4, borderRadius: 2 }} />
              <Typography variant="caption" color="text.secondary" align="center" display="block" sx={{ mt: 1 }}>
                {t('dashboard.cards.tariffClassifier.input.analyzing')}
              </Typography>
            </Box>
          )}
        </form>

        {error && (
          <Alert severity="error" sx={{ mt: 2, borderRadius: 1.5 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
      </Paper>

      {/* Result Card */}
      {result && (
        <Fade in timeout={400}>
          <Card
            elevation={0}
            sx={{
              border: '2px solid',
              borderColor: brand.success,
              borderRadius: 2,
              overflow: 'visible',
            }}
          >
            <CardContent sx={{ p: 3 }}>
              {/* Header row */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <SuccessIcon color="success" />
                <Typography variant="h6" fontWeight={700}>
                  {t('dashboard.cards.tariffClassifier.classificationResult')}
                </Typography>
                {/* Rule badge — shown when a user-defined tariff rule matched */}
                {!isAdvancedResponse(result) && result.rule_applied && (
                  <Chip
                    label={`Rule: ${result.rule_name || 'Custom Rule'}`}
                    size="small"
                    color="secondary"
                    variant="outlined"
                    sx={{ fontWeight: 700, fontSize: '0.75rem' }}
                  />
                )}
                {/* Match-strength badge — the top result's own score, distinct
                    from the per-level agreement shown in the ladder below. */}
                <Tooltip
                  title="Match strength of the top result. The ladder below shows how much the candidates agree at each level — a separate measure."
                  arrow
                >
                  <Chip
                    label={`${(result.confidence * 100).toFixed(0)}% match`}
                    size="small"
                    color={getConfidenceColor(result.confidence)}
                    sx={{ ml: 'auto', fontWeight: 700, fontSize: '0.8rem', px: 0.5 }}
                  />
                </Tooltip>
              </Box>

              <Divider sx={{ mb: 2.5 }} />

              {/* HS Code display */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 3,
                  mb: 3,
                  p: 2.5,
                  bgcolor: getConfidenceBg(result.confidence),
                  borderRadius: 2,
                  flexWrap: 'wrap',
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} letterSpacing={1} sx={{ textTransform: 'uppercase' }}>
                    {t('dashboard.cards.tariffClassifier.generatedHSCode')}
                  </Typography>
                  <Typography
                    variant="h3"
                    fontWeight={800}
                    sx={{ fontFamily: 'monospace', letterSpacing: 2, mt: 0.5, color: brand.orange }}
                  >
                    {formatHSCode(hsCode || '')}
                  </Typography>
                </Box>

                <Button
                  variant={copied ? 'contained' : 'outlined'}
                  color={copied ? 'success' : 'primary'}
                  size="small"
                  startIcon={copied ? <VerifiedIcon /> : <CopyIcon />}
                  onClick={handleCopyHSCode}
                  sx={{ textTransform: 'none', borderRadius: 1.5, whiteSpace: 'nowrap' }}
                >
                  {copied ? t('dashboard.cards.tariffClassifier.copiedToClipboard') : t('dashboard.cards.tariffClassifier.copyHSCode')}
                </Button>
              </Box>

              {/* 2→4→6→8 hierarchy ladder with per-level confidence + correct buttons */}
              {hierarchy && (
                <HierarchyLadder
                  hierarchy={hierarchy}
                  hasFeedback={hasFeedback}
                  onCorrect={handleCorrectLevel}
                />
              )}

              {/* Description */}
              {description && (
                <Box sx={{ mb: 2.5 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} letterSpacing={0.5}>
                    {t('dashboard.cards.tariffClassifier.enhancedDescription')}
                  </Typography>
                  <Paper
                    elevation={0}
                    sx={{ p: 1.5, bgcolor: 'grey.50', mt: 0.75, borderRadius: 1.5, border: '1px solid', borderColor: 'grey.200' }}
                  >
                    <Typography variant="body2">{description}</Typography>
                  </Paper>
                </Box>
              )}

              {/* Reasoning */}
              {isAdvancedResponse(result) && result.reasoning && (
                <Box sx={{ mb: 2.5 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} letterSpacing={0.5}>
                    {t('dashboard.cards.tariffClassifier.reasoning')}
                  </Typography>
                  <Paper
                    elevation={0}
                    sx={{ p: 1.5, bgcolor: 'primary.50', mt: 0.75, borderRadius: 1.5, border: '1px solid', borderColor: 'primary.100' }}
                  >
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
                      {result.reasoning}
                    </Typography>
                  </Paper>
                </Box>
              )}

              {/* AI Pipeline section */}
              {isAdvancedResponse(result) && result.rag_details && (
                <Box
                  sx={{
                    mt: 2.5,
                    p: 2,
                    bgcolor: 'grey.50',
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'grey.200',
                  }}
                >
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    color="text.secondary"
                    letterSpacing={1}
                    sx={{ textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}
                  >
                    <ScienceIcon fontSize="inherit" sx={{ fontSize: '1rem' }} />
                    {t('dashboard.cards.tariffClassifier.analysis.logic')}
                  </Typography>

                  <Grid container spacing={2} alignItems="center">
                    {pathInfo && (
                      <>
                        <Grid item xs={12} sm={5}>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                            {t('dashboard.cards.tariffClassifier.analysis.pathTaken')}
                          </Typography>
                          <Chip
                            label={pathInfo.label}
                            size="small"
                            color={pathInfo.color}
                            icon={<RouteIcon />}
                            sx={{ fontWeight: 600 }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={7}>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                            {t('dashboard.cards.tariffClassifier.analysis.verdict')}
                          </Typography>
                          <Typography variant="body2" fontWeight={600} color="text.primary">
                            {pathInfo.desc}
                          </Typography>
                        </Grid>
                      </>
                    )}
                  </Grid>
                </Box>
              )}

              {/* Footer: processing time + mode pill + feedback button */}
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="caption" color="text.disabled">
                  {(result as any).processing_time_ms
                    ? t('dashboard.cards.tariffClassifier.processingTimeSeconds', {
                        seconds: ((result as any).processing_time_ms / 1000).toFixed(2),
                      })
                    : t('dashboard.cards.tariffClassifier.processedViaRag')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  {pathInfo && (
                    <Chip
                      label={pathInfo.label}
                      size="small"
                      color={pathInfo.color}
                      variant="outlined"
                      sx={{ fontSize: '0.7rem' }}
                    />
                  )}
                  {/* Whole-code feedback fallback — only when there's no ladder
                      (rule override / no-match). When the ladder is shown, its
                      per-rung "Correct?" buttons cover this, so we hide it to
                      avoid a redundant second path. Gated by the feature flag. */}
                  {hasFeedback && !hierarchy && (
                    <Tooltip title="Report an incorrect HS code result" arrow>
                      <Button
                        id="tariff-feedback-button"
                        size="small"
                        variant="outlined"
                        color="warning"
                        startIcon={<FlagIcon fontSize="small" />}
                        onClick={handleFeedbackOpen}
                        sx={{
                          textTransform: 'none',
                          fontSize: '0.75rem',
                          borderRadius: 1.5,
                          py: 0.4,
                        }}
                      >
                        Incorrect result?
                      </Button>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Fade>
      )}

      {/* EU Shipping Compliance */}
      {result && hsCode && <ShippingVerdictPanel hsCode={hsCode} />}

      {/* ── Feedback / Correction Dialog ─────────────────────────────────── */}
      <Dialog
        open={feedbackOpen}
        onClose={() => !feedbackSubmitting && setFeedbackOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}>
          <FlagIcon color="warning" fontSize="small" />
          <Typography fontWeight={700} sx={{ flex: 1 }}>
            Correct the {LEVEL_LABEL[correctionLevel]}
          </Typography>
          <IconButton
            size="small"
            onClick={() => setFeedbackOpen(false)}
            disabled={feedbackSubmitting}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2.5, borderRadius: 1.5 }}>
            Your correction is applied <strong>immediately</strong> — future classifications
            for similar descriptions will use the updated code.
          </Alert>

          {/* Read-only: original description */}
          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
            PRODUCT DESCRIPTION
          </Typography>
          <Paper
            elevation={0}
            sx={{ p: 1.5, mb: 2.5, bgcolor: 'grey.50', borderRadius: 1.5, border: '1px solid', borderColor: 'grey.200' }}
          >
            <Typography variant="body2" color="text.secondary">
              {formData.product_description}
            </Typography>
          </Paper>

          {/* Read-only: what the system returned AT THIS LEVEL */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
              SYSTEM RETURNED ({LEVEL_LABEL[correctionLevel]})
            </Typography>
            <Chip
              label={currentLevelCode || '—'}
              size="small"
              color="error"
              variant="outlined"
              sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.85rem' }}
            />
          </Box>

          {/* Quick-pick: ★ recommended (model alternatives) first, then the full
              set of valid options at this level. */}
          {(childOptions.length > 0 || loadingChildren) && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.25 }}>
                PICK A VALID {LEVEL_LABEL[correctionLevel].toUpperCase()}
                {loadingChildren && <CircularProgress size={12} sx={{ ml: 1 }} />}
              </Typography>
              <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 0.75 }}>
                ★ = recommended by the model. Not right? Pick any valid option below or type it.
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, maxHeight: 168, overflowY: 'auto', p: 0.5 }}>
                {childOptions.map((opt) => {
                  const isRec = recommendedCodes.includes(opt.code);
                  const isSelected = correctHsCode === opt.code;
                  return (
                    <Tooltip key={opt.code} title={opt.title || ''} arrow>
                      <Chip
                        label={isRec ? `★ ${opt.code}` : opt.code}
                        size="small"
                        onClick={() => {
                          setCorrectHsCode(opt.code);
                          setCorrectHsCodeError('');
                        }}
                        color={isSelected || isRec ? 'warning' : 'default'}
                        variant={isSelected ? 'filled' : 'outlined'}
                        sx={{ fontFamily: 'monospace', fontWeight: 600, cursor: 'pointer' }}
                      />
                    </Tooltip>
                  );
                })}
              </Box>
            </Box>
          )}

          {/* Correct code input (manual, or refine a pick) */}
          <TextField
            id="feedback-correct-hs-code"
            label={`Correct ${LEVEL_LABEL[correctionLevel]} *`}
            placeholder={'0'.repeat(maxCodeLen)}
            fullWidth
            value={correctHsCode}
            onChange={(e) => {
              setCorrectHsCode(e.target.value.replace(/\D/g, '').slice(0, maxCodeLen));
              setCorrectHsCodeError('');
            }}
            error={!!correctHsCodeError}
            helperText={
              correctHsCodeError ||
              (correctionLevel === 'cn8' ? '6–10 digits, numbers only' : `${maxCodeLen} digits, numbers only`)
            }
            inputProps={{ maxLength: maxCodeLen, inputMode: 'numeric', pattern: '[0-9]*' }}
            sx={{ mb: 2 }}
          />

          {/* Optional note */}
          <TextField
            id="feedback-user-note"
            label="Note (optional)"
            placeholder="Why is this code wrong? What clues did we miss?"
            fullWidth
            multiline
            rows={2}
            value={feedbackNote}
            onChange={(e) => setFeedbackNote(e.target.value)}
            inputProps={{ maxLength: 2000 }}
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setFeedbackOpen(false)}
            disabled={feedbackSubmitting}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            id="feedback-submit-button"
            variant="contained"
            color="warning"
            onClick={handleFeedbackSubmit}
            disabled={feedbackSubmitting || !correctHsCode}
            startIcon={feedbackSubmitting ? <CircularProgress size={16} color="inherit" /> : <FlagIcon />}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5 }}
          >
            {feedbackSubmitting ? 'Submitting…' : 'Submit Correction'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DirectClassificationForm;
